/**
 * Unified FilterBar configuration for ALL views
 * This ensures visual consistency across the entire app
 */

import type { FilterConfig, ActionButton, FilterOption } from '../components/FilterBar';

// Type options - same across all views
export const TYPE_OPTIONS: FilterOption[] = [
  { value: 'Notes', label: 'Notes' },
  { value: 'Resource', label: 'Resource' },
  { value: 'Content', label: 'Content' }
];

// Intent options - for read_later/implement_later filtering
export const INTENT_OPTIONS: FilterOption[] = [
  { value: 'read_later', label: 'Read Later' },
  { value: 'implement_later', label: 'Apply Later' },
  { value: 'reference', label: 'Reference' },
  { value: 'archived', label: 'Archived' }
];

// Engagement filter options (views, likes)
export const VIEWS_OPTIONS: FilterOption[] = [
  { value: 'any', label: 'Any Views' },
  { value: '10k', label: '> 10K' },
  { value: '100k', label: '> 100K' },
  { value: '1m', label: '> 1M' },
  { value: '10m', label: '> 10M' }
];

export const LIKES_OPTIONS: FilterOption[] = [
  { value: 'any', label: 'Any Likes' },
  { value: '1k', label: '> 1K' },
  { value: '10k', label: '> 10K' },
  { value: '100k', label: '> 100K' },
  { value: '1m', label: '> 1M' }
];

// Category options - ALL categories flat list for filter bar
export const CATEGORY_OPTIONS: FilterOption[] = [
  // Main categories
  { value: 'Programming', label: 'Programming' },
  { value: 'Content Creation', label: 'Content Creation' },
  { value: 'Personal Brand', label: 'Personal Brand' },
  { value: 'Business', label: 'Business' },
  { value: 'Marketing', label: 'Marketing' },
  { value: 'Paid Ads', label: 'Paid Ads' },
  { value: 'Video & Recording', label: 'Video & Recording' },
  { value: 'AI Prompts', label: 'AI Prompts' },
  { value: 'Personal Dev', label: 'Personal Dev' },
  { value: 'Side Projects', label: 'Side Projects' },
  { value: 'GTM & AdsConcierge', label: 'GTM & AdsConcierge' },
  { value: 'Journaling', label: 'Journaling' },
  { value: 'Datasets', label: 'Datasets' },
  // Content Creation subcategories
  { value: 'Inspiration', label: '↳ Inspiration' },
  { value: 'Content Ideas', label: '↳ Content Ideas' },
  { value: 'Tropicalizar', label: '↳ Tropicalizar' },
  { value: 'Hooks', label: '↳ Hooks' },
  { value: 'Frameworks', label: '↳ Frameworks' },
  { value: 'Loops', label: '↳ Loops' },
  { value: 'Stakes', label: '↳ Stakes' },
  { value: 'CTA', label: '↳ CTA' },
  { value: 'Packages', label: '↳ Packages' },
  { value: 'Angles', label: '↳ Angles' },
  { value: 'Scripts', label: '↳ Scripts' },
  // Datasets subcategories
  { value: 'Dataset/Ads', label: '↳ Dataset/Ads' },
  { value: 'Dataset/Reels', label: '↳ Dataset/Reels' },
  { value: 'Dataset/Stories', label: '↳ Dataset/Stories' },
  { value: 'Dataset/Vlog', label: '↳ Dataset/Vlog' },
  { value: 'Dataset/Thumbnails', label: '↳ Dataset/Thumbnails' },
  // Other
  { value: 'Uncategorized', label: 'Uncategorized' }
];

// Unified filters - SAME for all views
export const UNIFIED_FILTERS: FilterConfig[] = [
  {
    id: 'type',
    icon: 'category',
    label: 'Type',
    type: 'checkbox',
    options: TYPE_OPTIONS,
    multiple: true
  },
  {
    id: 'category',
    icon: 'folder',
    label: 'Category',
    type: 'checkbox',
    options: CATEGORY_OPTIONS,
    multiple: true
  },
  {
    id: 'tags',
    icon: 'tag',
    label: 'Tags',
    type: 'tags',
    options: [], // Populated dynamically
    multiple: true
  },
  {
    id: 'views',
    icon: 'visibility',
    label: 'Views',
    type: 'radio',
    options: VIEWS_OPTIONS,
    multiple: false
  },
  {
    id: 'likes',
    icon: 'favorite',
    label: 'Likes',
    type: 'radio',
    options: LIKES_OPTIONS,
    multiple: false
  }
  // Note: Intent filter removed from filter bar - use sidebar quick filter buttons instead
];

// Unified actions - SAME for all views (based on Knowledge Base bar)
export const UNIFIED_ACTIONS: ActionButton[] = [
  { id: 'refresh', icon: 'refresh', title: 'Refresh' },
  { id: 'menu', icon: 'more_vert', title: 'Menu' },
  { id: 'add', icon: 'add', title: 'Add New' },
  { id: 'upload', icon: 'cloud_upload', title: 'Upload' },
  { id: 'preview', icon: 'visibility', title: 'Preview' },
  { id: 'review', icon: 'rate_review', title: 'Review' },
  { id: 'transcribe', icon: 'closed_caption', title: 'Transcribe' },
  { id: 'export', icon: 'download', title: 'Export' }
];

// ============================================
// PATTERNS VIEW specific configuration
// ============================================

// Pattern type filter options
export const PATTERN_TYPE_OPTIONS: FilterOption[] = [
  { value: 'all', label: 'All Elements' },
  { value: 'hook', label: 'Hooks' },
  { value: 'script', label: 'Script Structure' },
  { value: 'visual', label: 'Visual' },
  { value: 'cta', label: 'CTA' },
  { value: 'concept', label: 'Concepts' }
];

// Pattern status filter options
export const PATTERN_STATUS_OPTIONS: FilterOption[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'emerging', label: 'Emerging' },
  { value: 'active', label: 'Active' },
  { value: 'saturated', label: 'Saturated' },
  { value: 'declining', label: 'Declining' }
];

// Pattern performance filter options
export const PATTERN_PERFORMANCE_OPTIONS: FilterOption[] = [
  { value: 'any', label: 'Any Performance' },
  { value: '10', label: '> 10%' },
  { value: '20', label: '> 20%' },
  { value: '50', label: '> 50%' }
];

// Patterns-specific filters
export const PATTERNS_FILTERS: FilterConfig[] = [
  {
    id: 'patternType',
    icon: 'category',
    label: 'Type',
    type: 'radio',
    options: PATTERN_TYPE_OPTIONS,
    multiple: false
  },
  {
    id: 'status',
    icon: 'trending_up',
    label: 'Status',
    type: 'checkbox',
    options: PATTERN_STATUS_OPTIONS.slice(1), // Remove 'all' for checkbox
    multiple: true
  },
  {
    id: 'performance',
    icon: 'speed',
    label: 'Performance',
    type: 'radio',
    options: PATTERN_PERFORMANCE_OPTIONS,
    multiple: false
  }
];

// Patterns-specific actions
export const PATTERNS_ACTIONS: ActionButton[] = [
  { id: 'analyze-trends', icon: 'insights', title: 'Analyze Trends' },
  { id: 'refresh', icon: 'refresh', title: 'Refresh' },
  { id: 'export', icon: 'download', title: 'Export' }
];
